import AudioPlayerContainer from "@features/session/containers/AudioPlayerContainer";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { isWeb } from "@memorang/helpers/platform";
import Box from "@memorang/ui/components/Box";
import Button from "@memorang/ui/components/Button";
import DialogWrapper from "@memorang/ui/components/DialogWrapper";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";
import { Checkbox, Dialog, List, Text } from "react-native-paper";

const AUDIO_URI =
	"https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/partners/toefl/tts-audio/tts_test-audio_1738156059281.mp3";

type Props = {
	showDialog: boolean;
	handleClose: () => void;
};

export const AudioPlaybackTestDialog = ({ showDialog, handleClose }: Props) => {
	const { t } = useTranslation();
	const theme = useAppTheme();
	const [expanded, setExpanded] = useState(false);
	const showAudioPlaybackTestDialog = useGlobalStore(
		(state) => state.showAudioPlaybackTestDialog,
	);
	const toggleAudioPlaybackTestDialog = useGlobalStore(
		(state) => state.toggleAudioPlaybackTestDialog,
	);

	const appName = useAppStore((state) => state.app.name);

	return (
		<DialogWrapper visible={showDialog} handleClose={handleClose}>
			<Dialog.Icon icon="music-note-eighth" />
			<Dialog.Title style={{ textAlign: "center" }}>
				{t("session.audioPlaybackTestDialog.title")}
			</Dialog.Title>
			<Dialog.ScrollArea
				style={{
					borderTopWidth: 0,
					borderBottomWidth: 0,
					paddingHorizontal: 0,
				}}
			>
				<ScrollContainer
					contentContainerStyle={{
						paddingHorizontal: 24,
					}}
				>
					<Box gap={16}>
						<Text variant="bodyMedium">
							{t("session.audioPlaybackTestDialog.description", {
								appName,
							})}
						</Text>
						<AudioPlayerContainer uri={AUDIO_URI} />
						<CustomMarkdownRenderer
							text={t("session.audioPlaybackTestDialog.textToHear", {
								text: "_**This is a test playback to ensure audio playback is audible.**_",
							})}
						/>
						<List.Accordion
							title={t("session.audioPlaybackTestDialog.troubleshooting.title")}
							titleStyle={theme.fonts.titleSmall}
							titleNumberOfLines={2}
							expanded={expanded}
							left={(props) => (
								<List.Icon {...props} icon="alert-circle-outline" />
							)}
							onPress={() => setExpanded(!expanded)}
							style={{
								paddingVertical: 0,
								backgroundColor: theme.colors.surfaceVariant,
							}}
						>
							<List.Item
								title={t(
									"session.audioPlaybackTestDialog.troubleshooting.volumeTitle",
								)}
								left={(props) => <List.Icon {...props} icon="volume-plus" />}
								description={
									isWeb
										? t(
												"session.audioPlaybackTestDialog.troubleshooting.volumeDescriptionWeb",
											)
										: t(
												"session.audioPlaybackTestDialog.troubleshooting.volumeDescriptionMobile",
											)
								}
								titleNumberOfLines={2}
								descriptionNumberOfLines={0}
							/>
							<List.Item
								left={(props) => <List.Icon {...props} icon="headphones" />}
								title={t(
									"session.audioPlaybackTestDialog.troubleshooting.outputTitle",
								)}
								description={t(
									"session.audioPlaybackTestDialog.troubleshooting.outputDescription",
								)}
								titleNumberOfLines={2}
								descriptionNumberOfLines={0}
							/>
							{isWeb && (
								<List.Item
									left={(props) => <List.Icon {...props} icon="volume-off" />}
									title={t(
										"session.audioPlaybackTestDialog.troubleshooting.browserTitle",
									)}
									description={t(
										"session.audioPlaybackTestDialog.troubleshooting.browserDescription",
									)}
									titleNumberOfLines={2}
									descriptionNumberOfLines={0}
								/>
							)}
						</List.Accordion>
						<Box flexDirection="row" alignItems="center">
							<Checkbox.Android
								status={showAudioPlaybackTestDialog ? "unchecked" : "checked"}
								onPress={toggleAudioPlaybackTestDialog}
							/>
							<Pressable onPress={toggleAudioPlaybackTestDialog}>
								<Text>{t("session.audioPlaybackTestDialog.skipCheck")}</Text>
							</Pressable>
						</Box>
					</Box>
				</ScrollContainer>
			</Dialog.ScrollArea>
			<Dialog.Actions>
				<Button
					mode="contained"
					onPress={handleClose}
					labelStyle={{
						fontWeight: 600,
					}}
				>
					{t("session.audioPlaybackTestDialog.workingButton")}
				</Button>
			</Dialog.Actions>
		</DialogWrapper>
	);
};
